import React from "react";
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

/**
 * ListMenu
 *
 * A reusable dropdown menu for selecting from a list of options.
 *
 * @param {Object} props
 * @param {HTMLElement|null} props.anchorEl - Anchor element for the menu
 * @param {() => void} props.onClose - Callback fired when the menu closes
 * @param {(value: string | number) => (event: React.MouseEvent) => void} props.onSelect
 *        Function that returns an onClick handler for a given option value
 * @param {Array<{
 *   value: string | number,
 *   label: string,
 *   icon?: React.ReactNode,
 *   color?: string,
 *   disabled?: boolean,
 *   selected?: boolean
 * }>} props.options - Menu options to render. `selected` highlights an option
 *        directly, for menus where several can be active at once
 * @param {string | number | null} [props.selectedValue] - Currently selected value
 * @param {string | null} [props.title] - Optional menu title
 * @param {string | null} [props.footerText] - Optional caption shown below the options
 * @param {boolean} [props.releaseFocus] - Let an option's handler move focus
 *        elsewhere (e.g. into a text field) without the menu pulling it back
 *        while open, or returning it to the anchor on close
 */
const ListMenu = ({
  anchorEl,
  onClose,
  onSelect,
  options,
  selectedValue = null,
  title = null,
  footerText = null,
  align = "left",
  TransitionProps,
  releaseFocus = false,
}) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    disableEnforceFocus={releaseFocus}
    disableRestoreFocus={releaseFocus}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: align === "right" ? "right" : "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: align === "right" ? "right" : "left",
    }}
    TransitionProps={TransitionProps}
    slotProps={{
      paper: {
        elevation: 0,
        sx: {
          overflow: "auto",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))", // adds shadow around menu
          mt: 1.5,
        },
      },
    }}
  >
    {title && (
      <Box sx={{ px: 1, pb: 0.5 }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {title}:
        </Typography>
      </Box>
    )}
    {options
      .flatMap((option) => [
        <MenuItem
          key={option.value}
          onClick={(event) => onSelect(option.value)(event)}
          selected={
            option.selected ??
            (selectedValue !== null && option.value === selectedValue)
          }
          disabled={option?.disabled}
          dense
        >
          {option?.icon && (
            <ListItemIcon sx={{ color: option?.color }}>
              {option?.icon}
            </ListItemIcon>
          )}
          {option.label}
        </MenuItem>,
        option.dividerAfter ? (
          <Divider key={`divider-${option.value}`} />
        ) : null,
      ])
      .filter(Boolean)}
    {footerText && (
      <Box sx={{ px: 2, pt: 0.5, pb: 0.5, maxWidth: 220 }}>
        <Typography variant="caption" color="text.secondary">
          {footerText}
        </Typography>
      </Box>
    )}
  </Menu>
);

export default ListMenu;
